import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, RefreshCw, Building2, Edit3, Trash2, Globe, MapPin, Mail, GraduationCap, DollarSign, X, Check, Loader2, Link as LinkIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useSelection, SelectionCheckbox, BulkActionBar } from './BulkSelect';

const emptyCollege = {
  name: '', location: '', country: '', website: '',
  representative_email: '', college_fee: 0, notes: '', programs: [],
};

const CollegeModal = ({ college, onClose, onSaved }) => {
  const { toast } = useToast();
  // Ensure programs have tier/duration defaults when editing a legacy college.
  const seedForm = () => {
    const base = college || emptyCollege;
    return {
      ...base,
      programs: (base.programs || []).map((p) => ({
        ...p,
        duration_years: p.duration_years || 1,
        commission_tiers: p.commission_tiers && p.commission_tiers.length > 0 ? p.commission_tiers : [
          { min_apps: 0, max_apps: 2, percent: 25 },
          { min_apps: 3, max_apps: 20, percent: 30 },
          { min_apps: 21, max_apps: 50, percent: 35 },
          { min_apps: 51, max_apps: null, percent: 40 },
        ],
      })),
    };
  };
  const [form, setForm] = useState(seedForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const DEFAULT_TIERS = [
    { min_apps: 0, max_apps: 2, percent: 25 },
    { min_apps: 3, max_apps: 20, percent: 30 },
    { min_apps: 21, max_apps: 50, percent: 35 },
    { min_apps: 51, max_apps: null, percent: 40 },
  ];
  const updateProgram = (i, k, v) => {
    const progs = [...form.programs];
    progs[i] = { ...progs[i], [k]: v };
    setForm({ ...form, programs: progs });
  };
  const addProgram = () => setForm({ ...form, programs: [...form.programs, { name: '', default_sale_price: 0, duration_years: 1, commission_tiers: [...DEFAULT_TIERS] }] });
  const removeProgram = (i) => setForm({ ...form, programs: form.programs.filter((_, idx) => idx !== i) });
  const updateTier = (pi, ti, k, v) => {
    const progs = [...form.programs];
    const tiers = [...(progs[pi].commission_tiers || [])];
    tiers[ti] = { ...tiers[ti], [k]: k === 'percent' || k === 'min_apps' ? (v === '' ? '' : Number(v)) : (v === '' || v === null ? null : Number(v)) };
    progs[pi] = { ...progs[pi], commission_tiers: tiers };
    setForm({ ...form, programs: progs });
  };
  const addTier = (pi) => {
    const progs = [...form.programs];
    const tiers = [...(progs[pi].commission_tiers || [])];
    const lastMax = tiers.at(-1)?.max_apps ?? 0;
    tiers.push({ min_apps: (lastMax || 0) + 1, max_apps: null, percent: 40 });
    progs[pi] = { ...progs[pi], commission_tiers: tiers };
    setForm({ ...form, programs: progs });
  };
  const removeTier = (pi, ti) => {
    const progs = [...form.programs];
    progs[pi] = { ...progs[pi], commission_tiers: (progs[pi].commission_tiers || []).filter((_, idx) => idx !== ti) };
    setForm({ ...form, programs: progs });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.location || !form.country) {
      toast({ title: 'Missing fields', description: 'Name, Location, and Country are required.' });
      return;
    }
    const payload = {
      ...form,
      college_fee: parseFloat(form.college_fee) || 0,
      representative_email: form.representative_email || null,
      programs: form.programs
        .filter((p) => (p.name || '').trim())
        .map((p) => ({
          name: p.name.trim(),
          default_sale_price: parseFloat(p.default_sale_price) || 0,
          duration_years: Math.max(1, parseInt(p.duration_years || 1, 10)),
          commission_tiers: (p.commission_tiers || [])
            .filter((t) => t.percent !== '' && t.percent !== null)
            .map((t) => ({
              min_apps: parseInt(t.min_apps || 0, 10),
              max_apps: t.max_apps === null || t.max_apps === '' ? null : parseInt(t.max_apps, 10),
              percent: parseFloat(t.percent) || 0,
            })),
        })),
    };
    setBusy(true);
    try {
      if (college?.id) {
        await api.put(`/admin/colleges/${college.id}`, payload);
        toast({ title: 'College updated' });
      } else {
        await api.post('/admin/colleges', payload);
        toast({ title: 'College created' });
      }
      onSaved?.();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Error' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl pointer-events-auto">
          <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">{college?.id ? 'Edit College' : 'Add College'}</div>
              <h3 className="font-display text-xl text-[#0B5E2A]">{form.name || 'New College'}</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]">
              <X className="h-5 w-5 pointer-events-none" />
            </button>
          </div>

          <form onSubmit={submit} className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="College Name *" value={form.name} onChange={onChange('name')} full />
              <Field label="Location *" value={form.location} onChange={onChange('location')} placeholder="City, Province" />
              <Field label="Country *" value={form.country} onChange={onChange('country')} placeholder="e.g. Canada" />
              <Field label="Website" value={form.website} onChange={onChange('website')} placeholder="https://..." type="url" />
              <Field label="Representative Email" value={form.representative_email} onChange={onChange('representative_email')} type="email" placeholder="rep@college.edu" />
            </div>

            <div className="rounded-lg border border-[#F0B93C]/40 bg-[#FEF8E6] p-4">
              <div className="flex items-center gap-2 text-[#8a5e00] text-sm font-semibold">
                <DollarSign className="h-4 w-4" /> Financial Details (internal — never shown to recruiters)
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="College Fee (CAD)" value={form.college_fee} onChange={onChange('college_fee')} type="number" min="0" step="50" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-[#2e4030]">Programs</label>
                <button type="button" onClick={addProgram} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#0B5E2A] text-white hover:bg-[#094a22] transition-colors">
                  <Plus className="h-3.5 w-3.5" /> Add Program
                </button>
              </div>
              {form.programs.length === 0 ? (
                <div className="text-sm text-[#6b7e6d] italic border border-dashed border-[#0B5E2A]/20 rounded-lg py-6 text-center">
                  No programs yet. Click "Add Program" to list degrees/courses offered.
                </div>
              ) : (
                <ul className="space-y-3">
                  {form.programs.map((p, i) => {
                    const tuition = parseFloat(p.default_sale_price) || 0;
                    const tiers = p.commission_tiers || [];
                    return (
                      <li key={i} className="border border-[#0B5E2A]/15 rounded-lg bg-white overflow-hidden">
                        {/* Program header row */}
                        <div className="flex items-center gap-2 bg-[#f6faf3] px-3 py-2.5 border-b border-[#0B5E2A]/10">
                          <GraduationCap className="h-4 w-4 brand-green shrink-0" />
                          <input
                            value={p.name}
                            onChange={(e) => updateProgram(i, 'name', e.target.value)}
                            placeholder="e.g. Diploma in Business Management"
                            data-testid={`program-name-${i}`}
                            className="flex-1 px-2 py-1.5 rounded border border-[#0B5E2A]/15 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 bg-white"
                          />
                          <div className="flex items-center gap-1 bg-white border border-[#0B5E2A]/20 rounded px-2 py-1 text-sm" title="Duration (years)">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={p.duration_years ?? 1}
                              onChange={(e) => updateProgram(i, 'duration_years', e.target.value)}
                              data-testid={`program-duration-${i}`}
                              className="w-12 bg-transparent focus:outline-none text-right"
                            />
                            <span className="text-xs text-[#6b7e6d]">yr</span>
                          </div>
                          <div className="flex items-center gap-1 bg-[#FEF8E6] border border-[#F0B93C]/40 rounded px-2 py-1 text-sm" title="Tuition / Sale price">
                            <span className="text-[#8a5e00]">$</span>
                            <input
                              type="number"
                              min="0"
                              step="50"
                              value={p.default_sale_price}
                              onChange={(e) => updateProgram(i, 'default_sale_price', e.target.value)}
                              placeholder="Tuition"
                              data-testid={`program-tuition-${i}`}
                              className="w-24 bg-transparent focus:outline-none"
                            />
                          </div>
                          <button type="button" onClick={() => removeProgram(i)} className="h-8 w-8 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center" title="Remove program">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {/* Tier schedule */}
                        <div className="px-3 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-wider text-[#6b7e6d] font-semibold">Commission Tier Schedule</div>
                            <button type="button" onClick={() => addTier(i)} data-testid={`program-add-tier-${i}`} className="text-[11px] font-semibold text-[#0B5E2A] hover:underline">+ Add Tier</button>
                          </div>
                          <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase text-[#6b7e6d]">
                              <tr>
                                <th className="text-left font-semibold py-1 w-20">Min Apps</th>
                                <th className="text-left font-semibold py-1 w-20">Max Apps</th>
                                <th className="text-left font-semibold py-1 w-16">%</th>
                                <th className="text-left font-semibold py-1">Equivalent $</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {tiers.map((t, ti) => {
                                const pct = parseFloat(t.percent) || 0;
                                const dollars = Math.round(tuition * pct / 100 * 100) / 100;
                                return (
                                  <tr key={ti} className="border-t border-[#0B5E2A]/5">
                                    <td className="py-1.5 pr-2">
                                      <input type="number" min="0" value={t.min_apps ?? 0} onChange={(e) => updateTier(i, ti, 'min_apps', e.target.value)} className="w-full px-2 py-1 rounded border border-[#0B5E2A]/15 bg-white" />
                                    </td>
                                    <td className="py-1.5 pr-2">
                                      <input
                                        type="number"
                                        min="0"
                                        placeholder="∞"
                                        value={t.max_apps == null ? '' : t.max_apps}
                                        onChange={(e) => updateTier(i, ti, 'max_apps', e.target.value)}
                                        className="w-full px-2 py-1 rounded border border-[#0B5E2A]/15 bg-white"
                                      />
                                    </td>
                                    <td className="py-1.5 pr-2">
                                      <input type="number" min="0" max="100" step="0.5" value={t.percent ?? 0} onChange={(e) => updateTier(i, ti, 'percent', e.target.value)} className="w-full px-2 py-1 rounded border border-[#0B5E2A]/15 bg-white" />
                                    </td>
                                    <td className="py-1.5 pr-2 font-semibold text-[#0B5E2A]">${dollars.toLocaleString()}</td>
                                    <td className="py-1.5">
                                      <button type="button" onClick={() => removeTier(i, ti)} className="h-6 w-6 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center" title="Remove tier">
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              {tiers.length === 0 && (
                                <tr><td colSpan={5} className="py-3 text-center text-[#6b7e6d] italic">Using default 25/30/35/40% schedule. Click "+ Add Tier" to customize.</td></tr>
                              )}
                            </tbody>
                          </table>
                          <p className="mt-2 text-[10px] text-[#6b7e6d]">Leave Max blank for the top tier (no cap). 40% is the ceiling on regular schedules — per-recruiter overrides can exceed.</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={onChange('notes')} rows={3} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 resize-y" />
            </div>

            <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">Cancel</button>
              <button type="submit" disabled={busy} className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>) : (<><Check className="h-4 w-4 pointer-events-none" /> Save College</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

const Field = ({ label, value, onChange, full, type = 'text', placeholder, min, step }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder} min={min} step={step} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
  </div>
);

const CollegesTab = ({ filter, refresh }) => {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const colSel = useSelection();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/colleges');
      setList(data);
    } catch (e) { /* 401 global */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  const onDelete = async (c) => {
    if (!window.confirm(`Move "${c.name}" to Trash?`)) return;
    try {
      await api.delete(`/admin/colleges/${c.id}`);
      toast({ title: 'Moved to Trash' });
      load();
    } catch (e) { toast({ title: 'Delete failed' }); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(colSel.selected);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} college${ids.length === 1 ? '' : 's'} to Trash?`)) return;
    try {
      const { data } = await api.post('/admin/colleges/bulk-delete', { ids });
      toast({ title: 'Moved to Trash', description: `${data.trashed} item(s) in the Trash tab.` });
      colSel.clear();
      load();
    } catch (e) { toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const q = (filter || '').toLowerCase();
  const filtered = list.filter((c) =>
    !q ||
    (c.name || '').toLowerCase().includes(q) ||
    (c.country || '').toLowerCase().includes(q) ||
    (c.location || '').toLowerCase().includes(q)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#4b5e4d]">
          <span className="font-semibold text-[#0B5E2A]">{filtered.length}</span> {filtered.length === 1 ? 'college' : 'colleges'}
        </div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B5E2A] text-white font-semibold text-sm hover:bg-[#094a22] transition-colors">
          <Plus className="h-4 w-4" /> Add College
        </button>
      </div>

      <BulkActionBar count={colSel.count} onClear={colSel.clear} onDelete={bulkDelete} itemType="college" testid="bulk-bar-colleges" />

      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[#6b7e6d]"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Building2 className="h-10 w-10 mx-auto brand-green opacity-60" />
            <div className="mt-3 font-display text-lg text-[#0B5E2A]">No colleges yet</div>
            <p className="mt-1 text-sm text-[#6b7e6d]">Add a college to make it available for student assignments.</p>
            <button onClick={() => setShowAdd(true)} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B5E2A] text-white font-semibold text-sm hover:bg-[#094a22] transition-colors">
              <Plus className="h-4 w-4" /> Add Your First College
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                <tr>
                  <Th className="w-10 text-center">
                    <SelectionCheckbox
                      checked={filtered.length > 0 && filtered.every((c) => colSel.has(c.id))}
                      onChange={() => colSel.toggleAll(filtered.map((c) => c.id))}
                      testid="col-select-all"
                      label="Select all colleges"
                    />
                  </Th>
                  <Th>College</Th><Th>Location</Th><Th>Programs</Th><Th>College Fee</Th><Th>Representative</Th><Th className="text-right pr-6">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className={`border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] cursor-pointer ${colSel.has(c.id) ? 'bg-[#f0f6ee]' : ''}`} onClick={() => setEditing(c)}>
                    <Td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <SelectionCheckbox
                        checked={colSel.has(c.id)}
                        onChange={() => colSel.toggle(c.id)}
                        testid={`col-select-${c.id}`}
                        label={`Select ${c.name}`}
                      />
                    </Td>
                    <Td>
                      <div className="font-semibold text-[#0B5E2A]">{c.name}</div>
                      {c.website && (
                        <a href={c.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-[#1e40af] hover:underline mt-0.5">
                          <LinkIcon className="h-3 w-3" /> {c.website.replace(/^https?:\/\//, '').replace(/\/$/, '').slice(0, 40)}
                        </a>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5 text-[#1b2a1f]"><MapPin className="h-3.5 w-3.5 text-[#6b7e6d]" />{c.location}</div>
                      <div className="text-xs text-[#6b7e6d] mt-0.5">{c.country}</div>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 bg-[#E1F3E4] text-[#0B5E2A] px-2.5 py-1 rounded-full text-xs font-bold">
                        <GraduationCap className="h-3.5 w-3.5" /> {c.program_count || 0}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-semibold text-[#1b2a1f]">${Number(c.college_fee || 0).toLocaleString()}</span>
                      <div className="text-[10px] uppercase tracking-wider text-[#8a5e00] bg-[#FEF8E6] inline-block px-1.5 py-0.5 rounded mt-1">Internal</div>
                    </Td>
                    <Td>
                      {c.representative_email ? (
                        <a href={`mailto:${c.representative_email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-[#1e40af] hover:underline">
                          <Mail className="h-3.5 w-3.5" /> {c.representative_email}
                        </a>
                      ) : (
                        <span className="text-xs text-[#6b7e6d] italic">Not set</span>
                      )}
                    </Td>
                    <Td className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setEditing(c)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#0B5E2A] text-white hover:bg-[#094a22] transition-colors">
                          <Edit3 className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button onClick={() => onDelete(c)} className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center" title="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showAdd || editing) && (
        <CollegeModal
          college={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>{children}</th>;
const Td = ({ children, className = '', onClick }) => <td onClick={onClick} className={`px-4 py-3 align-top ${className}`}>{children}</td>;

export default CollegesTab;
